import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Check, Users, Mail } from 'lucide-react';
interface Technician {
  id: string;
  name: string;
  distributorId: string;
  distributorName: string;
  email: string;
  phone: string;
}
interface Distributor {
  id: string;
  name: string;
  rings: number;
  stripePublishableKey: string;
  stripeSecretKey: string;
}
interface TechniciansManagerProps {
  technicians: Technician[];
  distributors: Distributor[];
  onAdd: (name: string, distributorName: string, email: string, phone: string) => Promise<void>;
  onEdit: (index: number, name: string, distributorName: string, email: string, phone: string) => Promise<void>;
  onDelete: (index: number) => Promise<void>;
  getDistributorColor: (name: string) => string;
}
export function TechniciansManager({
  technicians,
  distributors,
  onAdd,
  onEdit,
  onDelete,
  getDistributorColor
}: TechniciansManagerProps) {
  const [newTechName, setNewTechName] = useState('');
  const [newTechDistributor, setNewTechDistributor] = useState(distributors[0]?.name || '');
  const [newTechEmail, setNewTechEmail] = useState('');
  const [newTechPhone, setNewTechPhone] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingDistributor, setEditingDistributor] = useState('');
  const [editingEmail, setEditingEmail] = useState('');
  const [editingPhone, setEditingPhone] = useState('');
  const handleAdd = async () => {
    if (newTechName.trim() && newTechDistributor) {
      await onAdd(newTechName.trim(), newTechDistributor, newTechEmail.trim(), newTechPhone);
      setNewTechName('');
      setNewTechEmail('');
      setNewTechPhone('');
      setNewTechDistributor(distributors[0]?.name || '');
    }
  };
  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditingValue(technicians[index].name);
    setEditingDistributor(technicians[index].distributorName);
    setEditingEmail(technicians[index].email);
    setEditingPhone(technicians[index].phone);
  };
  const saveEdit = async () => {
    if (editingIndex !== null && editingValue.trim() && editingDistributor) {
      await onEdit(editingIndex, editingValue.trim(), editingDistributor, editingEmail.trim(), editingPhone);
      setEditingIndex(null);
      setEditingValue('');
      setEditingDistributor('');
      setEditingEmail('');
      setEditingPhone('');
    }
  };
  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
    setEditingDistributor('');
    setEditingEmail('');
    setEditingPhone('');
  };
  return <div className="p-6" data-id="element-1816">
      <div className="mb-6" data-id="element-1817">
        <h2 className="text-xl font-semibold text-slate-800 mb-2" data-id="element-1818">
          Manage Technicians
        </h2>
        <p className="text-sm text-slate-600" data-id="element-1819">
          Add, edit, or remove technicians and assign them to distributors
        </p>
      </div>

      {/* Add New Technician */}
      <div className="bg-slate-50 rounded-lg p-4 mb-6" data-id="element-1820">
        <h3 className="text-sm font-medium text-slate-700 mb-3" data-id="element-1821">
          Add New Technician
        </h3>
        <div className="space-y-3" data-id="element-1822">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1823">
            <input type="text" value={newTechName} onChange={e => setNewTechName(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAdd()} placeholder="Enter technician name..." className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1824" />
            <select value={newTechDistributor} onChange={e => setNewTechDistributor(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1825">
              {distributors.map(dist => <option key={dist.name} value={dist.name} data-id="element-1826">
                  {dist.name}
                </option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1827">
            <input type="email" value={newTechEmail} onChange={e => setNewTechEmail(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAdd()} placeholder="Email address..." className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1828" />
            <input type="tel" value={newTechPhone} onChange={e => setNewTechPhone(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAdd()} placeholder="Phone number..." className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1829" />
          </div>
          <button onClick={handleAdd} disabled={!newTechName.trim()} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed" data-id="element-1830">
            <Plus size={18} data-id="element-1831" />
            Add Technician
          </button>
        </div>
      </div>

      {/* Technician List */}
      <div className="space-y-2" data-id="element-1832">
        <h3 className="text-sm font-medium text-slate-700 mb-3" data-id="element-1833">
          Current Technicians ({technicians.length})
        </h3>
        {technicians.map((tech, index) => <div key={index} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors" data-id="element-1834">
            {editingIndex === index ? <div className="flex-1 space-y-3" data-id="element-1835">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1836">
                  <input type="text" value={editingValue} onChange={e => setEditingValue(e.target.value)} onKeyPress={e => e.key === 'Enter' && saveEdit()} placeholder="Name" className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" autoFocus data-id="element-1837" />
                  <select value={editingDistributor} onChange={e => setEditingDistributor(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1838">
                    {distributors.map(dist => <option key={dist.name} value={dist.name} data-id="element-1839">
                        {dist.name}
                      </option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1840">
                  <input type="email" value={editingEmail} onChange={e => setEditingEmail(e.target.value)} onKeyPress={e => e.key === 'Enter' && saveEdit()} placeholder="Email" className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1841" />
                  <input type="tel" value={editingPhone} onChange={e => setEditingPhone(e.target.value)} onKeyPress={e => e.key === 'Enter' && saveEdit()} placeholder="Phone" className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1842" />
                </div>
                <div className="flex items-center gap-2" data-id="element-1843">
                  <button onClick={saveEdit} className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2" data-id="element-1844">
                    <Check size={18} data-id="element-1845" />
                    Save
                  </button>
                  <button onClick={cancelEdit} className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-1846">
                    Cancel
                  </button>
                </div>
              </div> : <>
                <div className="flex-1" data-id="element-1847">
                  <div className="flex items-center gap-3 mb-2" data-id="element-1848">
                    <span className="text-slate-800 font-medium" data-id="element-1849">
                      {tech.name}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded border ${getDistributorColor(tech.distributorName)}`} data-id="element-1850">
                      {tech.distributorName}
                    </span>
                  </div>
                  {(tech.email || tech.phone) && <div className="space-y-1 text-sm text-slate-600" data-id="element-1851">
                      {tech.email && <div className="flex items-center gap-2" data-id="element-1852">
                          <Mail size={14} className="text-slate-400" data-id="element-1853" />
                          <span data-id="element-1854">{tech.email}</span>
                        </div>}
                      {tech.phone && <div className="flex items-center gap-2" data-id="element-1855">
                          <span className="text-slate-400" data-id="element-1856">📞</span>
                          <span data-id="element-1857">{tech.phone}</span>
                        </div>}
                    </div>}
                </div>
                <div className="flex items-center gap-2" data-id="element-1858">
                  <button onClick={() => startEditing(index)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit" data-id="element-1859">
                    <Edit2 size={16} data-id="element-1860" />
                  </button>
                  <button onClick={() => onDelete(index)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-id="element-1861">
                    <Trash2 size={16} data-id="element-1862" />
                  </button>
                </div>
              </>}
          </div>)}
      </div>

      {technicians.length === 0 && <div className="text-center py-12" data-id="element-1863">
          <Users size={48} className="text-slate-300 mx-auto mb-4" data-id="element-1864" />
          <p className="text-slate-600" data-id="element-1865">No technicians added yet</p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1866">
            Add your first technician above
          </p>
        </div>}
    </div>;
}